// Utils
import {
  ValidateUtility as Validate
} from '../utils/validate'

/**
 * @typedef Option
 * @type {Object}
 * @property {String} name  : Type name
 * @property {String} value : Type value
 */

/**
 * @typedef LocationDetail
 * @type {Object}
 * @property {String} zipCode
 * @property {String} tumbonPrefix
 * @property {String} tumbonName
 * @property {String} amphurPrefix
 * @property {String} amphurName
 * @property {String} provinceName
 */

/**
 * @typedef Location
 * @type {Object}
 * @property {String} zipCode
 * @property {String} tumbonCode
 * @property {String} tumbonPrefix
 * @property {String} tumbonName
 * @property {String} amphurPrefix
 * @property {String} amphurCode
 * @property {String} amphurName
 * @property {String} provinceName
 * @property {String} provinceCode
 */

export class NonScCommon {

  /** @type {Array<Option>} */
  static category = [
    {
      name  : 'แนวราบ',
      value : 'NSL'
    },
    {
      name  : 'แนวสูง',
      value : 'NSH'
    },
  ]

  /** @type {Location} */
  static location = {
    zipCode       : null,
    tumbonCode    : null,
    tumbonPrefix  : null,
    tumbonName    : null,
    amphurPrefix  : null,
    amphurCode    : null,
    amphurName    : null,
    provinceName  : null,
    provinceCode  : null
  }

  /** @type {Array<Option>} */
  static role = [
    {
      name  : 'ผู้ถือกรรมสิทธิ์',
      value : 'OWNER'
    },
    {
      name  : 'ผู้อยู่อาศัย',
      value : 'RESIDENT'
    },
    {
      name  : 'ผู้เช่า',
      value : 'RENTER'
    },
  ]

  /** @type {Array<Option>} */
  static construct = [
    {
      name  : 'บ้านเดี่ยว',
      value : 'HO'
    },
    {
      name  : 'บ้านแฝด',
      value : 'HO'
    },
    {
      name  : 'คอนโดมิเนียม',
      value : 'CO'
    },
    {
      name  : 'ทาวน์เฮ้าส์',
      value : 'TH'
    },
  ]

  static setRole(input) {
    return this.role.find(
      e => e.name === input || e.value === input
    )
  }
  
  /**
   * Set location
   * @param {*} input - Input
   * @returns {LocationDetail} location props
   */
  static setLocation(input) {

    // Validate Input
    if (!input) return null
    
    // Location Validator
    const isLocation = (
      Validate.isLocation(input)
    )

    // Validate Address
    if (!isLocation) return null

    // Address Props
    const address = [
      'tumbonPrefix',
      'tumbonName',
      'amphurPrefix',
      'amphurName',
      'provinceName' ,
      'zipCode',
    ]

    return address.reduce(
      (e, key) => ({ ...e, [key]: input[key] }), {}
    )
  }

  /**
   * Usable area payload value
   * @param {*} input - Area input
   * @returns {Number|null} Area, or null when not given
   */
  static area(input) {

    // Empty means "not given"; 0 must stay 0 so it is rejected, not dropped
    if (Validate.isEmpty(input)) return null

    return parseFloat(input)
  }

  /**
   * Error message mapper
   * @param {*} error - Error code
   * @returns {String} Error message 
   */
  static error(errorInput = 'TRY_AGAIN') {
    let errorCode = errorInput

    // Error messages
    const messages = {
      
      // Project
      NONSC_PROJECT_ADDRESS_INVALID     : 'ที่อยู่โครงการไม่ถูกต้อง',
      NONSC_PROJECT_CODE_INVALID        : 'รหัสโครงการไม่ถูกต้อง',
      NONSC_PROJECT_CODE_CONFLICT       : 'รหัสโครงการซ้ำ',
      NONSC_PROJECT_ID_INVALID          : 'เลขรหัสโครงการไม่ถูกต้อง',
      NONSC_PROJECT_IMAGE_TYPE_INVALID  : 'ประเภทรูปโครงการไม่ถูกต้อง',
      NONSC_PROJECT_IMAGE_URL_INVALID   : 'ลิงก์รูปโครงการไม่ถูกต้อง',
      NONSC_PROJECT_NAME_INVALID        : 'ชื่อโครงการไม่ถูกต้อง',
      NONSC_PROJECT_NOT_FOUND           : 'ไม่พบโครงการ',
      NONSC_PROJECT_PIN_INVALID         : 'พิกัดโครงการไม่ถูกต้อง',
      NONSC_PROJECT_TYPE_INVALID        : 'ประเภทโครงการไม่ถูกต้อง',

      // Home
      NONSC_HOME_ADDRESS_INVALID        : 'ที่อยู่บ้านไม่ถูกต้อง',
      NONSC_HOME_AREA_INVALID           : 'พื้นที่ใช้สอยไม่ถูกต้อง',
      NONSC_HOME_DUPLICATE              : 'ข้อมูลบ้านซ้ำ',
      NONSC_HOME_EXISTS                 : 'ข้อมูลบ้านมีอยู่แล้ว',
      NONSC_HOME_PROJECT_INVALID        : 'ข้อมูลโครงการไม่ถูกต้อง',
      NONSC_HOME_TRANSFER_DATE_INVALID  : 'วันที่โอนไม่ถูกต้อง',
      NONSC_HOME_TYPE_INVALID           : 'ประเภทบ้านไม่ถูกต้อง',
      
      // User
      NONSC_USER_EMAIL_INVALID          : 'อีเมล์ลูกบ้านไม่ถูกต้อง',
      NONSC_USER_EMAIL_CONFLICT         : 'อีเมล์ลูกบ้านซ้ำ',
      NONSC_USER_EMAIL_EXISTS           : 'อีเมล์ลูกบ้านมีอยู่แล้ว',
      NONSC_USER_HOME_INVALID           : 'ข้อมูลบ้านไม่ถูกต้อง',
      NONSC_USER_MOBILE_NUMBER_INVALID  : 'เบอร์มือถือลูกบ้านไม่ถูกต้อง',
      NONSC_USER_MOBILE_NUMBER_CONFLICT : 'เบอร์มือถือลูกบ้านซ้ำ',
      NONSC_USER_MOBILE_NUMBER_EXISTS   : 'เบอร์มือถือลูกบ้านมีอยู่แล้ว',
      NONSC_USER_PROJECT_INVALID        : 'ข้อมูลโครงการไม่ถูกต้อง',
      NONSC_USER_ROLE_INVALID           : 'ข้อมูลสถานะไม่ถูกต้อง',
      NONSC_USER_INVALID                : 'ข้อมูลลูกบ้านไม่ถูกต้อง',
      NONSC_USER_DELETE_FAILED          : 'ลบข้อมูลลูกบ้านไม่สำเร็จ โปรดลองอีกครั้ง',

      // Request
      BAD_REQUEST                       : 'ข้อมูลไม่ถูกต้อง โปรดตรวจสอบอีกครั้ง',
      TRY_AGAIN                         : 'มีบางอย่างผิดพลาด โปรดลองอีกครั้ง',
    }

    // Instance Validator
    const isInstance = (
      errorInput && errorInput.message
    )

    // Validate Instance
    if (isInstance) {
      errorCode = errorInput.message || 'TRY_AGAIN'
    }

    // Error Code Validator
    const isError = (
      Object.keys(messages).includes(errorCode)
    )

    return (
      isError ? messages[errorCode] : messages.TRY_AGAIN
    )
  }
}

